import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SendIcon from "@mui/icons-material/Send";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { confirmAlert } from "react-confirm-alert";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { findAll } from "../ComunicadoCategoria/api-comunicado-categoria";
import { save, send } from "./api-comunicado";
import SimpleDialogMessage from "../../../util/SimpleDialogMessage/SimpleDialogMessage";
import { open } from "../../../util/SimpleDialogMessage/actions/actions";
import { ADMIN, COORDENADOR } from "../../Usuario/Perfil";
import { decreaseQuantAprovacaoComunicado, increaseQuantAprovacaoComunicado } from "./actions/actions";
import ComunicadoBannerDialog from "../ComunicadoBanner/ComunicadoBannerDialog";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import { errorToast, success } from "../../../util/ToastUtil";
import { dateTimeFormat } from "../../../util/DateUtil";
import SunEditorUI from "../../UI/SunEditorUI";
import TextFieldDateTime from "../../UI/TextFieldDateTime";

const ALUNO_KEY = "Aluno";
const TURMA_KEY = "Turma";
const ENVIAR_POR = [ALUNO_KEY, TURMA_KEY];

const ENVIAR_PARA_ALUNO = ["Ambos", "Alunos", "Responsáveis"];
const ENVIAR_PARA_TURMA = [...ENVIAR_PARA_ALUNO, "Professores"];

const SEND_ERRORS = {
  "No turma registered": "Comunicado não possui turmas cadastradas para o envio.",
  "No aluno registered": "Comunicado não possui alunos cadastrados para o envio.",
};

const getEnviarParaOptions = (enviarPor) =>
  enviarPor === ALUNO_KEY ? ENVIAR_PARA_ALUNO : ENVIAR_PARA_TURMA;

const SectionTitle = ({ children }) => (
  <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: "uppercase" }}>
    {children}
  </Typography>
);

const InfoItem = ({ label, value }) => (
  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
    <Typography variant="caption" color="text.secondary" component="div">
      {label}
    </Typography>
    <Typography variant="body2">{value || "--"}</Typography>
  </Grid>
);

export default function ComunicadoForm({ comunicado, setComunicado, onSaved, handleDelete }) {
  const [categorias, setCategorias] = useState([]);
  const [message, setMessage] = useState({});
  const [isPerforming, setIsPerforming] = useState(false);
  const [openComunicadoBanner, setOpenComunicadoBanner] = useState(false);
  const usuario = useSelector((state) => state.usuario);
  const dispatch = useDispatch();

  const comunicadoAprovacao = usuario.instituicao.comunicadoAprovacao;
  const podeAprovar =
    comunicado.codigo &&
    comunicadoAprovacao &&
    (usuario.perfil === ADMIN || usuario.perfil === COORDENADOR);
  const enviarParaOptions = getEnviarParaOptions(comunicado.enviarPor);

  useEffect(() => {
    findAll().then((data) => {
      if (Array.isArray(data)) {
        setCategorias(data);
      } else if (data?.error) {
        errorMessage(data.message);
      }
    });
  }, []);

  const updateField = (name, value) => {
    setComunicado((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleChange = (event) => {
    updateField(event.target.name, event.target.value);
  };

  const handleChangeDescricao = (value) => {
    updateField("descricao", value.replace(/[​-‍﻿]/g, ""));
  };

  const handleEnviarPorChange = (event) => {
    const enviarPor = event.target.value;
    setComunicado((prevState) => ({
      ...prevState,
      enviarPor,
      // "Professores" só existe para envio por Turma.
      enviarPara: getEnviarParaOptions(enviarPor).includes(prevState.enviarPara)
        ? prevState.enviarPara
        : undefined,
    }));
  };

  const handleCategoriaChange = (event) => {
    updateField(
      "categoria",
      categorias.find((categoria) => categoria.codigo === event.target.value),
    );
  };

  const handleSwitchChange = (event) => {
    updateField(event.target.name, event.target.checked);
  };

  const handleComunicadoBanner = (cb) => {
    setOpenComunicadoBanner(false);
    updateField("comunicadoBanner", cb);
  };

  const validate = () => {
    if (!comunicado.enviarPara) {
      errorToast("Selecione para quem será enviado o comunicado.");
      return false;
    }
    if (!comunicado.categoria) {
      errorToast("Selecione a categoria.");
      return false;
    }
    if (comunicado.agendado && !comunicado.dataHoraAgendamento) {
      errorToast("Informe a data e hora do agendamento.");
      return false;
    }
    return true;
  };

  const clickSubmit = (event) => {
    event.preventDefault();
    if (!validate()) return;

    const eNovo = comunicado.eNovo;
    setIsPerforming(true);
    save(comunicado)
      .then((data) => {
        if (!data) return;
        if (data.error) {
          errorMessage(data.message);
          return;
        }
        if (eNovo) {
          dispatch(increaseQuantAprovacaoComunicado());
        }
        success("Salvo com sucesso.");
        onSaved(data, eNovo);
        setComunicado(data);
      })
      .finally(() => setIsPerforming(false));
  };

  const handleAprove = () => {
    const persist = { ...comunicado, aprovado: !comunicado.aprovado };
    setIsPerforming(true);
    save(persist)
      .then((data) => {
        if (!data) return;
        if (data.error) {
          errorMessage(data.message);
          return;
        }
        dispatch(
          data.aprovado ? decreaseQuantAprovacaoComunicado() : increaseQuantAprovacaoComunicado(),
        );
        success(data.aprovado ? "Comunicado Aprovado com Sucesso." : "Aprovação Removida com Sucesso.");
        onSaved(data, false);
        setComunicado(data);
      })
      .finally(() => setIsPerforming(false));
  };

  const sendComunicado = () => {
    setIsPerforming(true);
    send(comunicado.codigo)
      .then((data) => {
        if (!data) return;
        if (data.error) {
          if (SEND_ERRORS[data.message]) {
            setMessage({
              title: "Comunicado não foi enviado",
              description: SEND_ERRORS[data.message],
            });
            dispatch(open());
          } else {
            errorMessage(data.message);
          }
          return;
        }
        success("Comunicado Enviado com Sucesso.");
        onSaved(data, false);
        setComunicado(data);
      })
      .finally(() => setIsPerforming(false));
  };

  const handleSend = () => {
    confirmAlert({
      title: "Alerta",
      message:
        "Deseja enviar este comunicado? Após confirmado esta operação não poderá ser desfeita.",
      buttons: [
        { label: "Sim", onClick: sendComunicado },
        { label: "Não", onClick: () => {} },
      ],
    });
  };

  return (
    <Box>
      <SimpleDialogMessage title={message.title} description={message.description} />
      <ComunicadoBannerDialog
        open={openComunicadoBanner}
        handleClose={() => setOpenComunicadoBanner(false)}
        setComunicadoBanner={handleComunicadoBanner}
      />

      <form onSubmit={clickSubmit}>
        <Stack spacing={3}>
          <Stack spacing={2}>
            <SectionTitle>Destinatários</SectionTitle>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="enviar-por-label">Enviar Por</InputLabel>
                  <Select
                    labelId="enviar-por-label"
                    label="Enviar Por"
                    disabled={comunicado.enviado}
                    value={comunicado.enviarPor || ""}
                    onChange={handleEnviarPorChange}
                  >
                    {ENVIAR_POR.map((ep) => (
                      <MenuItem key={ep} value={ep}>
                        {ep}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="enviar-para-label">Enviar Para</InputLabel>
                  <Select
                    labelId="enviar-para-label"
                    label="Enviar Para"
                    name="enviarPara"
                    disabled={comunicado.enviado}
                    value={comunicado.enviarPara || ""}
                    onChange={handleChange}
                  >
                    {enviarParaOptions.map((ep) => (
                      <MenuItem key={ep} value={ep}>
                        {ep}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="categoria-label">Categoria</InputLabel>
                  <Select
                    labelId="categoria-label"
                    label="Categoria"
                    value={categorias.length > 0 && comunicado.categoria ? comunicado.categoria.codigo : ""}
                    onChange={handleCategoriaChange}
                  >
                    {categorias.map((categoria) => (
                      <MenuItem key={categoria.codigo} value={categoria.codigo}>
                        {categoria.descricao}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Stack>

          <Divider />

          <Stack spacing={2}>
            <SectionTitle>Conteúdo</SectionTitle>
            <TextField
              variant="outlined"
              size="small"
              required
              fullWidth
              label="Título"
              name="titulo"
              onChange={handleChange}
              value={comunicado.titulo || ""}
              slotProps={{ htmlInput: { maxLength: 100 } }}
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
              <Box
                sx={{
                  width: 240,
                  aspectRatio: "16 / 9",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 2,
                  overflow: "hidden",
                  bgcolor: "action.hover",
                  border: 1,
                  borderColor: "divider",
                  color: "text.secondary",
                }}
              >
                {comunicado.comunicadoBanner ? (
                  <Box
                    component="img"
                    src={comunicado.comunicadoBanner.urlBanner}
                    alt="Banner do comunicado"
                    sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                  />
                ) : (
                  <Stack alignItems="center" spacing={0.5}>
                    <ImageOutlinedIcon />
                    <Typography variant="caption">Sem banner</Typography>
                  </Stack>
                )}
              </Box>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={() => setOpenComunicadoBanner(true)}>
                  {comunicado.comunicadoBanner ? "Trocar Banner" : "Selecionar Banner"}
                </Button>
                {comunicado.comunicadoBanner && (
                  <Button color="inherit" onClick={() => updateField("comunicadoBanner", null)}>
                    Remover Banner
                  </Button>
                )}
              </Stack>
            </Stack>

            <Box>
              <TextFieldDateTime
                label="Data Hora Para"
                value={comunicado.dataHoraPara || null}
                onChange={(value) => updateField("dataHoraPara", dayjs(value))}
                slotProps={{ textField: { size: "small" } }}
              />
              <FormHelperText>
                Para quando será a entrega da Atividade/Lição. Caso não possua, manter a data atual.
              </FormHelperText>
            </Box>

            <SunEditorUI onChange={handleChangeDescricao} content={comunicado.descricao || ""} />
          </Stack>

          <Divider />

          <Stack spacing={2}>
            <SectionTitle>Agendamento</SectionTitle>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
              <FormControlLabel
                control={
                  <Switch
                    name="agendado"
                    checked={!!comunicado.agendado}
                    onChange={handleSwitchChange}
                  />
                }
                label="Agendar envio"
              />
              {comunicado.agendado && (
                <TextFieldDateTime
                  required
                  label="Data Hora Agendamento"
                  value={comunicado.dataHoraAgendamento || null}
                  onChange={(value) => updateField("dataHoraAgendamento", dayjs(value))}
                  slotProps={{ textField: { size: "small" } }}
                />
              )}
            </Stack>
          </Stack>

          {comunicado.usuario && (
            <>
              <Divider />
              <Stack spacing={2}>
                <SectionTitle>Informações</SectionTitle>
                <Grid container spacing={2}>
                  <InfoItem
                    label="Criado por"
                    value={`${comunicado.usuario.nome} - ${comunicado.usuario.perfil}`}
                  />
                  <InfoItem label="Criado em" value={dateTimeFormat(comunicado.createdAt)} />
                  <InfoItem label="Última Atualização" value={dateTimeFormat(comunicado.lastModified)} />
                  <InfoItem label="Enviado" value={comunicado.enviado ? "Sim" : "Não"} />
                  <InfoItem label="Finalizado Envio" value={comunicado.finalizadoEnvio ? "Sim" : "Não"} />
                </Grid>
              </Stack>
            </>
          )}

          <Divider />

          <Stack
            direction={{ xs: "column-reverse", sm: "row" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              {comunicado.codigo && (
                <Button
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  disabled={isPerforming}
                  onClick={() => handleDelete(comunicado.codigo)}
                >
                  Remover
                </Button>
              )}
            </Box>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              {podeAprovar && (
                <Button
                  variant="outlined"
                  color="secondary"
                  disabled={comunicado.enviado || isPerforming}
                  onClick={handleAprove}
                >
                  {comunicado.aprovado ? "Remover Aprovação" : "Aprovar"}
                </Button>
              )}
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveOutlinedIcon />}
                disabled={isPerforming}
              >
                Salvar
              </Button>
              {!comunicado.agendado && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<SendIcon />}
                  disabled={
                    (comunicadoAprovacao && !comunicado.aprovado) || !comunicado.codigo || isPerforming
                  }
                  onClick={handleSend}
                >
                  Enviar
                </Button>
              )}
            </Stack>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
}
